import {Component, Element, h, Listen, Prop} from '@stencil/core';
import {getGuidedSearchCategory, pushToDataLayer} from '../util';
import type {GuidedSearchCompleted} from '../../guided/api';
import { ChipType, type GuidedChipEventDetail } from '../../guided2/subcomponents/chips-carousel/model';

@Component({
  tag: 'uiaas-search-analytics-reporting-guided-v2',
})
export class UiaasSearchAnalyticsReportingGuidedComponentv2 {
    @Prop() readonly digitalPlatform?: string;
    @Element() element: HTMLElement;


    private async getSearchKeyword() {
        try {
          const inputElement = await document?.querySelector('uiaas-guided-search-v2')?.getDatafluxUnsafe();
          return await inputElement?.root?.searchTerm ?? '';
        } catch (error) {
          return '';
        }
    }

    private readonly EVENTS_NAMES = {
      guidedSearchClick: 'guidedSearchClick',
      search: 'search',
      onSiteSearch: 'OnSiteSearch'
    };

    @Listen('guided:chipClicked')
    guidedV2FilterClickedEventHandler(event: GuidedChipEventDetail & CustomEvent) {
        if(event?.detail.chipType == ChipType.GuidedV2Sections){
          pushToDataLayer({
            event: this.EVENTS_NAMES.onSiteSearch,
            site_search_category: 'Guided Search',
            site_search_action: 'click_filter',
            site_search_label: `category:${event?.detail.label}`,
            search_location: 'Site Search',
          });
        }

        if(event?.detail.chipType == ChipType.GuidedV2Suggestions && !event?.detail.isPromoted) {
          pushToDataLayer({
            event: this.EVENTS_NAMES.guidedSearchClick,
            guidedSearchKeyword: event?.detail.label,
            guidedSearchCategory: 'Guided Search: Most popular searches',
            site_search_label: 'Guided Search v2',
            search_location: 'Site Search',
            digitalPlatform: this.digitalPlatform || 'se main'
          });
        }
    }

    // TBD in events
    @Listen('guidedV2:SectionChanged')
    categoryClickedEventHandler(event: CustomEvent) {
        pushToDataLayer({
          event: this.EVENTS_NAMES.onSiteSearch,
          site_search_category: 'Guided Search',
          site_search_action: 'view_all',
          site_search_label: `category:${event.detail?.label}`,
          search_location: 'Site Search',
        });
    }

    @Listen('guidedV2:SearchCompleted')
    async searchCompletedEventHandler(event: GuidedSearchCompleted & CustomEvent) {
        const searchKeyword = await this.getSearchKeyword();
        if (event.detail.event === this.EVENTS_NAMES.guidedSearchClick) {

          pushToDataLayer({
            event: this.EVENTS_NAMES.guidedSearchClick,
            guidedSearchKeyword: searchKeyword,
            guidedSearchCategory: `Guided Search:${event.detail.category}`,
            site_search_label: 'Guided Search v2',
            destinationUrl: event.detail.link,
            search_location: 'Site Search',
            digitalPlatform: this.digitalPlatform || 'se main'
          });

          pushToDataLayer({
            event: this.EVENTS_NAMES.search,
            site_search_category: `${getGuidedSearchCategory(event.target)}Guided Search`,
            guidedSearch: true,
            kw55: searchKeyword,
            refinedSearch: false,
            site_search_label: 'Guided Search v2',
            search_location: 'Site Search',
            digitalPlatform: this.digitalPlatform || 'se main'
          });
        }

    }

    @Listen('guidedV2:HistoryClicked')
    guidedV2HistoryClickedEventHandler(event: CustomEvent) {
        pushToDataLayer({
          event: this.EVENTS_NAMES.guidedSearchClick,
          guidedSearchKeyword: event.detail.term,
          guidedSearchCategory: 'Guided Search: Search history',
          site_search_label: 'Guided Search v2',
          search_location: 'Site Search',
          digitalPlatform: this.digitalPlatform || 'se main'
        });
    }

    @Listen('conversational-search:ga-cta-banner-clicked', { target: 'window' })
    conversationalSearchCtaBannerEventHandler(event: MouseEvent | CustomEvent) {
        if (event.detail.isGuided) {
          pushToDataLayer({
            event: 'genAI_search',
            search_keyword: event.detail.keyword,
            action: 'click',
            search_location: 'Site Search',
            label: [
              'isRefinement:false',
              'guidedSearchVersion:v2'
            ].join('###')
          });
        }
    }

    @Listen('uiaas:serachBarB2cClicked', { target: 'window' })
    searchBarB2cClickedEventHandler() {
        pushToDataLayer({
          event: 'OnSiteSearch',
          site_search_category: 'Search_bar',
          site_search_action: 'click',
          site_search_label: 'target:searchbar',
          site_search_keyword: '',
          search_location: 'Site Search',
        });
    }

    @Listen('guidedV2:SearchBarClicked', { target: 'window' })
    guidedV2SearchBarClickedEventHandler() {
        pushToDataLayer({
          event: 'OnSiteSearch',
          site_search_category: 'Search_bar',
          site_search_action: 'click',
          site_search_label: 'target:searchbar',
          site_search_keyword: ''
        });
    }

    @Listen('guided2:ChipClicked', { target: 'window' })
    chipClicked(event: GuidedChipEventDetail & CustomEvent) {
        if(event?.detail?.isPromoted){
          pushToDataLayer({
            event: 'guidedSearchClick',
            guidedSearchCategory: 'Guided Search: promotions - popular-searches',
            guidedSearchKeyword: event?.detail?.label,
            search_location: 'Site Search',
            destination_url: `https://www.se.com/${event?.detail?.country}/${event?.detail?.language}/search/?q=${event?.detail?.label}`,
            site_search_category: 'Guided Search',
            site_search_label: 'Guided Search v2'
          });
        }
    }

    @Listen('guidedV2:SelectedSuggestionClicked', { target: 'window' })
    selectedSuggestionClicked(event: CustomEvent) {
        pushToDataLayer({
          event: 'guidedSearchClick',
          guidedSearchCategory: 'Guided Search: promotions - suggestions',
          search_location: 'Site Search',
          destination_url: event.detail.suggestionURL,
          site_search_category: 'Guided Search',
          site_search_label: 'Guided Search v2',
        });
        pushToDataLayer({
          event: 'search',
          site_search_category: 'Guided Search',
          guidedSearch: true,
          site_search_keyword: '',
          refinedSearch: false,
          site_search_results: '',
          site_search_label: 'Guided Search promotions',
          search_location: 'Site Search',
        });
    }

    render() {
        return <slot/>;
    }
}
